import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { EventApiService } from '../../services/event-api/EventApiService';
import { EventExpenseApiService } from '../../services/event-expense-api/EventExpenseApiService';
import {
  EventExpense,
  EventExpenseCategory,
  EventExpenseScope,
  SaveEventExpenseData,
  SportsEvent,
} from '../../types/Event';

type ExpenseSortField = 'title' | 'scope' | 'category' | 'binding' | 'amount' | 'responsible';

@Component({
  selector: 'app-expense-directory-page',
  standalone: false,
  templateUrl: './expense-directory-page.component.html',
  styleUrl: './expense-directory-page.component.sass',
})
export class ExpenseDirectoryPageComponent implements OnInit {
  public mode: 'list' | 'create' | 'edit' = 'list';
  public expenseId: string | null = null;
  public expenses: EventExpense[] = [];
  public events: SportsEvent[] = [];
  public categories: EventExpenseCategory[] = [];
  public formData: SaveEventExpenseData = this.createEmptyForm();
  public editingExpense: EventExpense | null = null;
  public loading = false;
  public saving = false;
  public sortField: ExpenseSortField = 'title';
  public sortDirection: 'asc' | 'desc' = 'asc';
  public errorMessage: string | null = null;
  public successMessage: string | null = null;

  public constructor(
    private eventApi: EventApiService,
    private expenseApi: EventExpenseApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
    private router: Router,
  ) { }

  public async ngOnInit(): Promise<void> {
    this.resolvePageMode();
    await Promise.all([
      this.loadEvents(),
      this.loadCategories(),
      this.loadExpenses(),
    ]);
    this.applyDefaults();
    if (this.mode === 'edit') {
      await this.loadEditingExpense();
    }
  }

  public get isListMode(): boolean {
    return this.mode === 'list';
  }

  public get pageTitle(): string {
    if (this.mode === 'create') {
      return 'Новый расход';
    }
    if (this.editingExpense) {
      return `Редактирование ${this.editingExpense.code}`;
    }

    return 'Расходы';
  }

  public get pageDescription(): string {
    return this.isListMode
      ? 'Расходы по событиям и месячные платежи, которые распределяются на события.'
      : 'Тип расхода, категория, сумма и привязка к событию или месяцу.';
  }

  public get activeEvents(): SportsEvent[] {
    return this.events
      .filter((event) => event.status === 'in_progress')
      .sort((left, right) => new Date(left.startsAt).getTime() - new Date(right.startsAt).getTime());
  }

  public get sortedExpenses(): EventExpense[] {
    return [...this.expenses].sort((left, right) => {
      const direction = this.sortDirection === 'asc' ? 1 : -1;
      return this.compareExpense(left, right, this.sortField) * direction;
    });
  }

  public get submitButtonLabel(): string {
    if (this.saving) {
      return this.mode === 'edit' ? 'Сохраняем...' : 'Создаем...';
    }

    return this.mode === 'edit' ? 'Сохранить изменения' : 'Создать расход';
  }

  public async saveExpense(): Promise<void> {
    const validationError = this.validateForm();
    if (validationError) {
      this.errorMessage = validationError;
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    this.successMessage = null;

    try {
      const payload = this.normalizeFormData();
      const response = this.mode === 'edit' && this.editingExpense
        ? await this.expenseApi.updateExpense(this.editingExpense.code, payload)
        : await this.expenseApi.createExpense(payload);
      const expense = this.unwrapResponse(response);
      if (!expense) {
        return;
      }

      this.notificationService.notify('finance', this.mode === 'edit' ? 'Расход обновлен' : 'Расход создан', `${expense.code}: ${expense.title}`);
      await this.router.navigate(['/dashboard/expenses']);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public async deleteExpense(): Promise<void> {
    if (!this.editingExpense) {
      return;
    }
    if (!window.confirm(`Удалить расход ${this.editingExpense.code}?`)) {
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    try {
      const response = await this.expenseApi.deleteExpense(this.editingExpense.code);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }

      this.notificationService.notify('finance', 'Расход удален', this.editingExpense.code);
      await this.router.navigate(['/dashboard/expenses']);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public setScope(scope: EventExpenseScope): void {
    this.formData.scope = scope;
    if (scope === 'event') {
      this.formData.month = null;
      this.formData.eventId ??= this.activeEvents[0]?.id ?? null;
      return;
    }

    this.formData.eventId = null;
    this.formData.month ??= new Date().toISOString().slice(0, 7);
  }

  public setSort(field: ExpenseSortField): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.sortField = field;
    this.sortDirection = 'asc';
  }

  public sortMark(field: ExpenseSortField): string {
    if (this.sortField !== field) {
      return '↕';
    }

    return this.sortDirection === 'asc' ? '↑' : '↓';
  }

  public sortState(field: ExpenseSortField): 'ascending' | 'descending' | 'none' {
    if (this.sortField !== field) {
      return 'none';
    }

    return this.sortDirection === 'asc' ? 'ascending' : 'descending';
  }

  public getExpenseRoute(expense: EventExpense): string {
    return expense.code || expense.id;
  }

  public getExpenseScopeName(scope: EventExpenseScope): string {
    return scope === 'monthly' ? 'На месяц' : 'На событие';
  }

  public getExpenseBinding(expense: EventExpense): string {
    if (expense.scope === 'monthly') {
      return expense.month ? this.formatMonth(expense.month) : 'Месяц не указан';
    }

    const event = this.events.find((item) => item.id === expense.eventId);
    return event ? `${event.homeTeam.name} - ${event.awayTeam.name}` : 'Событие не найдено';
  }

  public getEventOptionLabel(event: SportsEvent): string {
    return `${this.formatDate(event.startsAt)} · ${event.homeTeam.name} - ${event.awayTeam.name}`;
  }

  public formatDate(value: string): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(value));
  }

  public formatMoney(value: number): string {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0,
    }).format(value);
  }

  public formatMonth(value: string): string {
    const [year, month] = value.split('-').map(Number);
    return new Intl.DateTimeFormat('ru-RU', {
      month: 'long',
      year: 'numeric',
    }).format(new Date(year, month - 1, 1));
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  private resolvePageMode(): void {
    const url = this.router.url.split('?')[0].replace(/\/$/, '');
    const editMatch = url.match(/\/dashboard\/expenses\/([^/]+)\/edit$/);
    if (editMatch) {
      this.mode = 'edit';
      this.expenseId = decodeURIComponent(editMatch[1]);
      return;
    }
    if (url.endsWith('/dashboard/expenses/new')) {
      this.mode = 'create';
      this.expenseId = null;
      return;
    }

    this.mode = 'list';
    this.expenseId = null;
  }

  private async loadExpenses(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;
    try {
      const response = await this.expenseApi.getExpenses();
      const expenses = this.unwrapResponse(response);
      if (expenses) {
        this.expenses = expenses;
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private async loadEditingExpense(): Promise<void> {
    if (!this.expenseId) {
      this.errorMessage = 'Расход не найден.';
      return;
    }

    this.loading = true;
    try {
      const response = await this.expenseApi.getExpense(this.expenseId);
      const expense = this.unwrapResponse(response);
      if (!expense) {
        this.errorMessage = 'Расход не найден.';
        return;
      }

      this.editingExpense = expense;
      this.formData = {
        scope: expense.scope,
        eventId: expense.eventId,
        month: expense.month,
        categoryId: expense.categoryId,
        title: expense.title,
        amount: expense.amount,
        comment: expense.comment,
        paidByUserId: expense.paidByUserId,
      };
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private async loadEvents(): Promise<void> {
    try {
      const response = await this.eventApi.getEvents();
      const events = this.unwrapResponse(response);
      if (events) {
        this.events = events;
      }
    } catch (error) {
      this.handleUnknownError(error);
    }
  }

  private async loadCategories(): Promise<void> {
    try {
      const response = await this.expenseApi.getExpenseCategories();
      const categories = this.unwrapResponse(response);
      if (categories) {
        this.categories = categories;
      }
    } catch (error) {
      this.handleUnknownError(error);
    }
  }

  private applyDefaults(): void {
    this.formData.categoryId ??= this.categories[0]?.id ?? null;
    if (this.formData.scope === 'event') {
      this.formData.eventId ??= this.activeEvents[0]?.id ?? null;
    }
  }

  private validateForm(): string | null {
    if (!this.formData.title.trim()) {
      return 'Укажите название расхода.';
    }
    if (!this.formData.categoryId) {
      return 'Выберите категорию расхода.';
    }
    if (!Number.isFinite(Number(this.formData.amount)) || Number(this.formData.amount) < 0) {
      return 'Укажите корректную сумму расхода.';
    }
    if (this.formData.scope === 'event' && !this.formData.eventId) {
      return 'Выберите событие для расхода.';
    }
    if (this.formData.scope === 'monthly' && !this.formData.month) {
      return 'Выберите месяц расхода.';
    }

    return null;
  }

  private normalizeFormData(): SaveEventExpenseData {
    return {
      scope: this.formData.scope,
      eventId: this.formData.scope === 'event' ? this.formData.eventId : null,
      month: this.formData.scope === 'monthly' ? this.formData.month : null,
      categoryId: this.formData.categoryId,
      title: this.formData.title.trim(),
      amount: Number(this.formData.amount) || 0,
      comment: this.formData.comment?.trim() ?? null,
    };
  }

  private createEmptyForm(): SaveEventExpenseData {
    return {
      scope: 'event',
      eventId: null,
      month: new Date().toISOString().slice(0, 7),
      categoryId: null,
      title: '',
      amount: 0,
      comment: null,
    };
  }

  private compareExpense(left: EventExpense, right: EventExpense, field: ExpenseSortField): number {
    if (field === 'amount') {
      return left.amount - right.amount;
    }
    if (field === 'scope') {
      return this.getExpenseScopeName(left.scope).localeCompare(this.getExpenseScopeName(right.scope), 'ru');
    }
    if (field === 'category') {
      return (left.category?.name ?? '').localeCompare(right.category?.name ?? '', 'ru');
    }
    if (field === 'binding') {
      return this.getExpenseBinding(left).localeCompare(this.getExpenseBinding(right), 'ru');
    }
    if (field === 'responsible') {
      const leftName = left.createdByUser?.displayName || left.createdByUser?.username || '';
      const rightName = right.createdByUser?.displayName || right.createdByUser?.username || '';
      return leftName.localeCompare(rightName, 'ru');
    }

    return left.title.localeCompare(right.title, 'ru');
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }
}
